import { Outlet, useMatches, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { usePermissions } from "@/auth/permissions";
import { visibleModules, visibleSections } from "@/auth/visible-navigation";
import { GlobalSearch } from "@/components/search/GlobalSearch";
import { AppSidebar } from "@/components/shell/AppSidebar";
import { FixtureDataBadge } from "@/components/shell/FixtureDataBadge";
import { Toaster } from "@/components/shell/Toaster";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { pathFor, useActiveSection } from "@/routes/useActiveSection";

/**
 * The shell wrapping every section route: the sidebar, then the page.
 *
 * Both levels of navigation live in the sidebar as of #346 — modules, with the
 * open module's sections under it. §11.1 specified a third column for the
 * section list; M2-9 replaced it with tabs in the page header, and those are
 * now gone too, because a phone has no room for a column the sidebar already
 * holds and no patience for two controls that can disagree about which section
 * is open.
 *
 * The shell still owns the route: the sidebar reports a choice and this turns
 * it into a URL.
 */
export function ShellLayout() {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { module, section } = useActiveSection();
  // Account settings sit in the shell without being a module's page; the
  // fallback `useActiveSection` gives would highlight the dashboard there.
  const onAccountPage = useMatches().some(
    (match) => match.staticData.accountPage === true,
  );

  const modules = useMemo(
    () =>
      visibleModules(permissions).map((visible) => ({
        module: visible,
        sections: visibleSections(visible, permissions),
      })),
    [permissions],
  );

  return (
    /*
      The viewport exactly, with the scrolling done inside: a list with a
      sticky header needs the scroll container to be the table rather than the
      page. `svh` rather than `vh` because a phone's address bar makes `vh`
      taller than what is on screen, which puts the pagination row under it.
    */
    <SidebarProvider className="h-svh overflow-hidden">
      <AppSidebar
        modules={modules}
        activeModuleId={onAccountPage ? "" : module.id}
        activeSectionId={onAccountPage ? "" : section.id}
        onSelect={(nextModule, nextSection) => {
          void navigate({ to: pathFor(nextModule, nextSection) });
        }}
      />

      <SidebarInset className="flex min-h-0 min-w-0 flex-col">
        <Outlet />
      </SidebarInset>

      <FixtureDataBadge />
      <Toaster />
      {/* One search panel for the whole shell (#640). */}
      <GlobalSearch />
    </SidebarProvider>
  );
}
